<template>
    <div class="competition-settings rebates">
        <div v-if="!ready" class="row mat32">
            <div class="col-12">
                <div class="admin-header__loading-state">
                    Trwa wczytywanie
                    <div class="mal15 lds-ellipsis">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            </div>
        </div>

        <template v-else>
            <panel-competition-form-box class="rebates-form__box mab16">
                <div class="col-12 col-lg-4">
                    <p class="rebates-form__label">Generowanie kodów rabatowych</p>
                    <panel-input-base-radio
                        v-model="form.type"
                        name="rebate-type"
                        :option="{ value: 'price', title: 'Rabat kwotowy' }"
                    />
                    <panel-input-base-radio
                        v-model="form.type"
                        name="rebate-type"
                        :option="{ value: 'start', title: 'Pełna opłata startowa' }"
                    />
                    <panel-input-base-radio
                        v-model="form.type"
                        name="rebate-type"
                        :option="{ value: 'mass', title: 'Multi kod' }"
                    />
                </div>
                <div class="col-12 col-lg-8">
                    <p class="rebates-form__hint">
                        Wartość kodu rabatowego może być kwotowa lub likwidować opłatę startową. Kody rabatowe
                        generowane są pulami. Określ wartość rabatu oraz ilość kodów. Do listy wygenerowanych kodów
                        możesz dodawać kolejne pule z inną wartością rabatu.
                    </p>
                    <div v-if="hasCycle" class="rebates-cycle">
                        Generujesz kody dla zawodów cyklicznych.<br />
                        Uczestnicy będą mogli użyć kodu w dowolnych zawodach w tym cyklu.
                    </div>
                    <div class="rebates-generate__row">
                        <div class="rebates-generate__amount" :class="{ 'is-disabled': form.type === 'start' }">
                            <p class="rebates-field-label">
                                <span>Rabat</span>
                                <span>zł</span>
                            </p>
                            <panel-input-text v-model="form.value" hint="0" :error="errors.value" />
                        </div>
                        <div class="rebates-generate__count">
                            <p class="rebates-field-label">Ilość</p>
                            <panel-input-text v-model="form.count" hint="0" :error="errors.count" />
                        </div>
                        <base-button btn1 class="rebates-generate__submit" :disabled="generating" @click="generate">
                            Wygeneruj kody
                        </base-button>
                    </div>
                    <div v-if="form.type === 'mass'" class="rebates-generate__mass">
                        <div class="rebates-generate__max">
                            <p class="rebates-field-label">Max. użyć</p>
                            <panel-input-text v-model="form.maxUses" hint="1" :error="errors.max_uses" />
                        </div>
                        <div class="rebates-generate__date">
                            <panel-input-date
                                v-model="form.validFrom"
                                label="Ważny od"
                                placeholder="Ważny od"
                                :error="errors.valid_from"
                            />
                        </div>
                        <div class="rebates-generate__date">
                            <panel-input-date
                                v-model="form.validTo"
                                label="Ważny do"
                                placeholder="Ważny do"
                                :error="errors.valid_to"
                            />
                        </div>
                    </div>
                    <p v-if="formError" class="rebates-form__error">{{ formError }}</p>
                </div>
            </panel-competition-form-box>

            <panel-competition-form-box v-if="features.combine" class="rebates-form__box mab16">
                <div class="col-12 col-lg-4">
                    <p class="rebates-form__label">Sumowanie kodów rabatowych</p>
                    <panel-input-base-radio
                        v-model="rebatesCombine"
                        name="rebates-combine"
                        :option="{ value: 'separate', title: 'Rabaty się nie sumują' }"
                    />
                    <panel-input-base-radio
                        v-model="rebatesCombine"
                        name="rebates-combine"
                        :option="{ value: 'sum', title: 'Rabaty się sumują' }"
                    />
                </div>
                <div class="col-12 col-lg-8">
                    <p class="rebates-form__hint">
                        Czy uczestnicy mają mieć możliwość wprowadzania wielu kodów rabatowych w koszyku? Jeśli suma
                        rabatów przekroczy wysokość opłaty startowej, rabat będzie naliczany dla produktów wybranych w
                        sklepie zawodów.
                    </p>
                </div>
            </panel-competition-form-box>

            <div ref="rebatesTable" class="rebate-list-card">
                <div v-if="!hasSelection" class="rebate-stats">
                    <div class="rebate-stats__box">
                        <div class="rebate-stats__title">Kody rabatowe niezrealizowane</div>
                        <div class="rebate-metrics">
                            <div class="rebate-metric">
                                <span>Ilość</span>
                                <strong>{{ unusedStats.count }}</strong>
                            </div>
                            <div class="rebate-metric">
                                <span>Wartość</span>
                                <strong>{{ formatPrice(unusedStats.value) }}</strong>
                            </div>
                        </div>
                    </div>
                    <div class="rebate-stats__box">
                        <div class="rebate-stats__title">Kody rabatowe zrealizowane</div>
                        <div class="rebate-metrics">
                            <div class="rebate-metric">
                                <span>Ilość</span>
                                <strong>{{ usedStats.count }}</strong>
                            </div>
                            <div class="rebate-metric">
                                <span>Wartość</span>
                                <strong>{{ formatPrice(usedStats.value) }}</strong>
                            </div>
                        </div>
                    </div>
                    <div class="rebate-stats__box">
                        <div class="rebate-stats__title">Filtr</div>
                        <div class="rebate-filters">
                            <div class="rebate-filter" :class="{ 'rebate-filter--filled': filters.valueKey }">
                                <button
                                    v-if="filters.valueKey"
                                    type="button"
                                    class="rebate-filter__clear"
                                    aria-label="Wyczyść kwotę"
                                    @click="filters.valueKey = null"
                                >
                                    <i class="far fa-times-circle" />
                                </button>
                                <panel-input-select
                                    v-model="valueFilterModel"
                                    :options="valueFilterOptions"
                                    label="text"
                                    track-by="value"
                                    placeholder="Kwota kodu"
                                    :hide-selected="false"
                                />
                            </div>
                            <div class="rebate-filter" :class="{ 'rebate-filter--filled': filters.status }">
                                <button
                                    v-if="filters.status"
                                    type="button"
                                    class="rebate-filter__clear"
                                    aria-label="Wyczyść status"
                                    @click="filters.status = null"
                                >
                                    <i class="far fa-times-circle" />
                                </button>
                                <panel-input-select
                                    v-model="statusFilterModel"
                                    :options="statusFilterOptions"
                                    label="text"
                                    track-by="value"
                                    placeholder="Status"
                                    :hide-selected="false"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-else
                    class="rebate-stats rebate-stats--selection"
                    :class="{ 'rebate-stats--selection-no-share': !features.share }"
                >
                    <div class="rebate-stats__box">
                        <div class="rebate-stats__title">Wybrane</div>
                        <div class="rebate-metrics">
                            <div class="rebate-metric">
                                <span>Ilość</span>
                                <strong>{{ selectedStats.count }}</strong>
                            </div>
                            <div class="rebate-metric">
                                <span>Wartość</span>
                                <strong>{{ formatPrice(selectedStats.value) }}</strong>
                            </div>
                        </div>
                    </div>
                    <div v-if="features.share" class="rebate-stats__box">
                        <div class="rebate-stats__title">Oznacz</div>
                        <div class="rebate-share" @click.capture="onSharePointer" @keydown.capture="onShareKey">
                            <panel-input-checkbox
                                :value="shareChecked"
                                :checkbox-value="true"
                                name="rebate-share"
                                variant="white"
                            >
                                Udostępniony
                            </panel-input-checkbox>
                        </div>
                    </div>
                    <div class="rebate-stats__box">
                        <div class="rebate-stats__title rebate-stats__title--ghost">Akcje</div>
                        <div class="rebate-actions">
                            <button type="button" class="rebate-action" @click="exportCsv">
                                <span>CSV</span>
                                <i class="fas fa-external-link-alt" aria-hidden="true" />
                            </button>
                            <button type="button" class="rebate-action" @click="copyCodes">
                                <span>Kopiuj</span>
                                <i class="fas fa-copy" aria-hidden="true" />
                            </button>
                            <button v-if="features.delete" type="button" class="rebate-action" @click="deleteSelected">
                                <span>Usuń</span>
                                <i class="fas fa-trash" aria-hidden="true" />
                            </button>
                            <button type="button" class="rebate-action rebate-action--close" @click="clearSelection">
                                Zamknij
                            </button>
                        </div>
                    </div>
                </div>

                <div class="rebate-table-wrap">
                    <base-table
                        class="rebate-table"
                        :headers="headers"
                        :items="tableItems"
                        :sort-column-default="1"
                        :sort-reverse-default="false"
                    >
                        <template v-slot:header="{ sort }">
                            <tr>
                                <th
                                    v-for="(header, column) in headers"
                                    :key="header.value"
                                    :class="[
                                        `col-${header.value}`,
                                        { 'is-static': header.value === 'index' || header.value === 'select' },
                                    ]"
                                    @click="header.value !== 'select' && header.value !== 'index' && sort(column)"
                                >
                                    <template v-if="header.value === 'select'">
                                        <span
                                            class="rebate-check rebate-check--header"
                                            @click.capture="toggleSelectAll"
                                        >
                                            <panel-input-checkbox
                                                :value="selectAll"
                                                :checkbox-value="true"
                                                name="rebate-select-all"
                                                variant="white"
                                            >
                                                &nbsp;
                                            </panel-input-checkbox>
                                        </span>
                                    </template>
                                    <template v-else>
                                        {{ header.text }}
                                        <i
                                            v-if="header.value === 'addedDateDisplay'"
                                            class="fas fa-list rebate-sort"
                                            aria-hidden="true"
                                        />
                                    </template>
                                </th>
                            </tr>
                        </template>
                        <template v-slot:body="{ displayedItems }">
                            <tr v-for="(item, row) in displayedItems" :key="item.id">
                                <td class="col-index">{{ row + 1 }}.</td>
                                <td class="col-addedDateDisplay">{{ item.addedDateDisplay }}</td>
                                <td class="col-valueDisplay">{{ item.valueDisplay }}</td>
                                <td class="col-code">
                                    <span class="rebate-code">{{ item.code }}</span>
                                    <span v-if="item.usesDisplay" class="rebate-uses">{{ item.usesDisplay }}</span>
                                </td>
                                <td class="col-playerName">{{ item.playerName }}</td>
                                <td class="col-usedDateDisplay">{{ item.usedDateDisplay }}</td>
                                <td class="col-statusLabel">
                                    <span class="rebate-status" :class="item.statusClass">
                                        <i v-if="item.statusIcon" :class="item.statusIcon" />
                                        {{ item.statusLabel }}
                                    </span>
                                </td>
                                <td class="col-select">
                                    <span
                                        v-if="item.selectable"
                                        class="rebate-check"
                                        @click.capture="toggleRow(item.id, $event)"
                                    >
                                        <panel-input-checkbox
                                            :value="selectedIds.includes(item.id)"
                                            :checkbox-value="true"
                                            :name="`rebate-${item.id}`"
                                        >
                                            &nbsp;
                                        </panel-input-checkbox>
                                    </span>
                                </td>
                            </tr>
                        </template>
                        <template v-slot:no-results>
                            <div class="rebate-empty">
                                {{ rebates.length ? 'Nie znaleziono' : 'Brak kodów rabatowych' }}
                            </div>
                        </template>
                    </base-table>
                </div>
            </div>

            <modal ref="deleteModal" body-class="h-auto" class="rebate-delete-modal">
                <template v-slot:header>Usuń wybrane kody rabatowe</template>
                <template v-slot:body>
                    <div class="rebate-delete-modal__body">
                        <p>Czy na pewno, chcesz usunąć wybrane kody rabatowe?</p>
                        <p>
                            Przy próbie skorzystania z usuniętego kodu uczestnik zostanie poinformowany, że kod jest
                            nieaktywny.
                        </p>
                    </div>
                </template>
                <template v-slot:footer>
                    <button type="button" class="rebate-delete-modal__cancel" @click="closeDeleteModal">Anuluj</button>
                    <base-button btn1 :disabled="deleting" @click="confirmDelete">Usuń wybrane kody</base-button>
                </template>
            </modal>
        </template>
    </div>
</template>

<script>
    import moment from 'moment';
    import { mapGetters } from 'vuex';
    import PanelCompetitionFormBox from '@/components/PanelCompetitionFormBox';
    import PanelInputBaseRadio from '@/components/PanelInputBaseRadio';
    import PanelInputCheckbox from '@/components/PanelInputCheckbox';
    import PanelInputDate from '@/components/PanelInputDate';
    import PanelInputSelect from '@/components/PanelInputSelect';
    import PanelInputText from '@/components/PanelInputText';
    import Modal from '@/components/Modal';
    import {
        REBATE_FEATURES,
        listRebates,
        generateRebates,
        deleteRebates,
        bulkUpdateRebatesStatus,
        updateRebatesCombine,
    } from '@/api/rebates';
    import { dataToCsv, downloadCsv } from '@/utils/files';

    const STATUS_LABELS = {
        available: 'Dostępny',
        shared: 'Udostępniony',
        entered: 'Wpisany',
        realized: 'Zrealizowany',
        unused: 'Dostępny',
        used: 'Zrealizowany',
        expired: 'Wygasły',
    };

    export default {
        name: 'PanelCompetitionRebates',
        components: {
            PanelCompetitionFormBox,
            PanelInputBaseRadio,
            PanelInputCheckbox,
            PanelInputDate,
            PanelInputSelect,
            PanelInputText,
            Modal,
        },
        props: {
            id: {
                type: Number,
                required: true,
            },
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                generating: false,
                sharing: false,
                deleting: false,
                combineReady: false,
                features: { ...REBATE_FEATURES },
                rebates: [],
                selectedIds: [],
                selectAll: false,
                shareChecked: false,
                pendingDeleteIds: [],
                rebatesCombine: 'separate',
                filters: {
                    valueKey: null,
                    status: null,
                },
                form: {
                    type: 'price',
                    value: '',
                    count: '',
                    maxUses: '',
                    validFrom: null,
                    validTo: null,
                },
                errors: {},
                headers: [
                    { text: '', value: 'index' },
                    { text: 'Data utw.', value: 'addedDateDisplay', type: 'date' },
                    { text: 'Kwota', value: 'valueDisplay' },
                    { text: 'Kod', value: 'code' },
                    { text: '', value: 'playerName' },
                    { text: '', value: 'usedDateDisplay', type: 'date' },
                    { text: 'Status', value: 'statusLabel' },
                    { text: '', value: 'select' },
                ],
            };
        },
        computed: {
            ...mapGetters(['competitionsSettings']),
            competitionSetting() {
                return this.competitionsSettings[this.id] || {};
            },
            hasCycle() {
                return Boolean(this.competition && this.competition.cycle);
            },
            hasSelection() {
                return this.selectedIds.length > 0;
            },
            formError() {
                return (
                    this.errors.value ||
                    this.errors.count ||
                    this.errors.max_uses ||
                    this.errors.valid_from ||
                    this.errors.valid_to ||
                    ''
                );
            },
            filteredRebates() {
                return this.rebates.filter((rebate) => {
                    if (this.filters.valueKey != null && this.filters.valueKey !== '') {
                        if (this.valueFilterKey(rebate) !== this.filters.valueKey) {
                            return false;
                        }
                    }
                    if (this.filters.status != null && this.filters.status !== '') {
                        if (this.resolveStatusKey(rebate) !== this.filters.status) {
                            return false;
                        }
                    }
                    return true;
                });
            },
            tableItems() {
                return this.filteredRebates.map((rebate) => this.mapRebateRow(rebate));
            },
            unusedStats() {
                return this.statsFor(this.rebates.filter((rebate) => this.isUnused(rebate)));
            },
            usedStats() {
                return this.statsFor(this.rebates.filter((rebate) => !this.isUnused(rebate)));
            },
            selectedRebates() {
                const ids = new Set(this.selectedIds);
                return this.rebates.filter((rebate) => ids.has(rebate.id));
            },
            selectedStats() {
                return this.statsFor(this.selectedRebates);
            },
            valueFilterOptions() {
                const keys = new Set();
                const options = [{ value: 'start', text: 'Start' }];
                this.rebates.forEach((rebate) => {
                    const key = this.valueFilterKey(rebate);
                    if (key !== 'start' && !keys.has(key)) {
                        keys.add(key);
                        options.push({
                            value: key,
                            text: this.formatPrice(Number(key)),
                        });
                    }
                });
                return options;
            },
            statusFilterOptions() {
                const keys = new Set(this.rebates.map((rebate) => this.resolveStatusKey(rebate)));
                return [...keys].map((key) => ({
                    value: key,
                    text: STATUS_LABELS[key] || key,
                }));
            },
            valueFilterModel: {
                get() {
                    if (this.filters.valueKey == null || this.filters.valueKey === '') {
                        return null;
                    }
                    return this.valueFilterOptions.find((option) => option.value === this.filters.valueKey) || null;
                },
                set(option) {
                    this.filters.valueKey = option && option.value != null ? option.value : null;
                },
            },
            statusFilterModel: {
                get() {
                    if (this.filters.status == null || this.filters.status === '') {
                        return null;
                    }
                    return this.statusFilterOptions.find((option) => option.value === this.filters.status) || null;
                },
                set(option) {
                    this.filters.status = option && option.value != null ? option.value : null;
                },
            },
        },
        watch: {
            'form.type': 'onFormType',
            rebatesCombine(value) {
                if (!this.combineReady) {
                    return;
                }
                this.saveCombine(value);
            },
            selectedIds() {
                this.syncShareCheckbox();
                const selectable = this.tableItems.filter((item) => item.selectable);
                this.selectAll =
                    selectable.length > 0 && selectable.every((item) => this.selectedIds.includes(item.id));
            },
        },
        async created() {
            this.initCombineFromSettings();
            this.combineReady = true;
            await this.loadRebates();
            this.ready = true;
        },
        methods: {
            onFormType(type) {
                if (type === 'start') {
                    this.form.value = '';
                }
            },
            initCombineFromSettings() {
                const value =
                    this.competitionSetting.rebatesCombine ?? this.competitionSetting.rebates_combine ?? false;
                this.rebatesCombine = value === true || value === 1 || value === '1' ? 'sum' : 'separate';
            },
            normalizeErrors(e) {
                const src = (e && e.errors) || e || {};
                if (!src || typeof src !== 'object' || Array.isArray(src)) {
                    return {};
                }
                const out = {};
                Object.keys(src).forEach((key) => {
                    if (key === 'error' || key === 'message') return;
                    const val = src[key];
                    out[key] = Array.isArray(val) ? val[0] : val;
                });
                return out;
            },
            async loadRebates({ preserveSelection = false } = {}) {
                const selected = preserveSelection ? this.selectedIds.slice() : [];
                try {
                    const data = await listRebates(this.id);
                    this.rebates = Array.isArray(data) ? data : [];
                    if (preserveSelection) {
                        const byId = new Map(this.rebates.map((rebate) => [rebate.id, rebate]));
                        this.selectedIds = selected.filter((rebateId) => {
                            const rebate = byId.get(rebateId);
                            return rebate && this.isSelectable(rebate);
                        });
                    } else {
                        this.clearSelection();
                    }
                } catch (e) {
                    console.error('Error loading rebates', e);
                    this.rebates = [];
                    alert('Nie udało się pobrać kodów rabatowych.');
                }
            },
            formatPrice(value) {
                const num = Number(value) || 0;
                return `${num.toFixed(2).replace('.', ',')} zł`;
            },
            formatDate(value) {
                if (value == null || value === '') return '';
                let m;
                if (typeof value === 'number' || /^\d+$/.test(String(value))) {
                    const n = Number(value);
                    m = moment(n < 1e12 ? n * 1000 : n);
                } else {
                    m = moment(value);
                }
                return m.isValid() ? m.format('DD.MM.YYYY') : '';
            },
            formatApiDate(value, endOfDay = false) {
                if (!value) return undefined;
                let m;
                if (moment.isMoment(value)) {
                    m = value.clone();
                } else if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
                    m = moment(value, 'DD.MM.YYYY', true);
                } else {
                    m = moment(value);
                }
                if (!m.isValid()) return undefined;
                if (endOfDay) {
                    m.endOf('day');
                } else {
                    m.startOf('day');
                }
                return m.format('YYYY-MM-DD HH:mm:ss');
            },
            isFullFee(rebate) {
                return rebate.type === 'full' || rebate.type === 'start';
            },
            rebatePrice(rebate) {
                if (this.isFullFee(rebate)) {
                    return 0;
                }
                const fromValue = Number(rebate.value);
                const fromPrice = Number(rebate.price);
                if (rebate.type === 'mass') {
                    return (!Number.isNaN(fromValue) && fromValue > 0 ? fromValue : fromPrice) || 0;
                }
                if (!Number.isNaN(fromPrice) && fromPrice > 0) return fromPrice;
                if (!Number.isNaN(fromValue) && fromValue > 0) return fromValue;
                return 0;
            },
            valueFilterKey(rebate) {
                if (this.isFullFee(rebate)) {
                    return 'start';
                }
                const price = this.rebatePrice(rebate);
                if (!price) {
                    return 'start';
                }
                return String(price);
            },
            resolveStatusKey(rebate) {
                const name = (rebate.statusName || '').toLowerCase();
                if (STATUS_LABELS[name]) {
                    if (name === 'unused') return 'available';
                    if (name === 'used') return 'realized';
                    return name;
                }
                if (rebate.status === 1) return 'available';
                if (rebate.status === 2) return 'shared';
                if (rebate.status === 3) return 'entered';
                if (rebate.status === 4) return 'realized';
                return 'available';
            },
            isUnused(rebate) {
                const key = this.resolveStatusKey(rebate);
                return key === 'available' || key === 'unused' || key === 'shared';
            },
            isSelectable(rebate) {
                return this.isUnused(rebate);
            },
            isDeletable(rebate) {
                return this.resolveStatusKey(rebate) === 'available';
            },
            statusClass(key) {
                if (key === 'unused' || key === 'available') return 'status-available';
                if (key === 'shared') return 'status-shared';
                if (key === 'entered') return 'status-entered';
                return 'status-used';
            },
            statusIcon(key) {
                if (key === 'unused' || key === 'available') return 'far fa-check-circle';
                if (key === 'shared') return 'far fa-arrow-circle-right';
                if (key === 'entered') return 'far fa-edit';
                return 'far fa-times-circle';
            },
            formatPlayerName(rebate) {
                if (Array.isArray(rebate.players) && rebate.players.length) {
                    return rebate.players
                        .map((player) => [player.firstname, player.lastname].filter(Boolean).join(' '))
                        .filter(Boolean)
                        .join(', ');
                }
                const player = rebate.player || {};
                return [player.firstname, player.lastname].filter(Boolean).join(' ');
            },
            mapRebateRow(rebate) {
                const statusKey = this.resolveStatusKey(rebate);
                const used = !this.isUnused(rebate);
                const playerName = used ? this.formatPlayerName(rebate) : '';
                let valueDisplay = 'Start';
                if (!this.isFullFee(rebate)) {
                    valueDisplay = this.formatPrice(this.rebatePrice(rebate));
                }
                let usesDisplay = '';
                if (rebate.type === 'mass' || (rebate.maxUses != null && Number(rebate.maxUses) > 1)) {
                    const current = rebate.currentUses != null ? rebate.currentUses : 0;
                    const max = rebate.maxUses != null ? rebate.maxUses : '?';
                    usesDisplay = `${current}/${max} użyć`;
                }
                const validFrom = this.formatDate(rebate.validFrom);
                const validTo = this.formatDate(rebate.validTo);
                if (validFrom || validTo) {
                    const range = [validFrom || '…', validTo || '…'].join(' – ');
                    usesDisplay = usesDisplay ? `${usesDisplay}, ${range}` : range;
                }
                return {
                    id: rebate.id,
                    code: rebate.code || rebate.codeRaw,
                    addedDateDisplay: this.formatDate(rebate.addedDate || rebate.addedTime),
                    usedDateDisplay: used ? this.formatDate(rebate.usedDate || rebate.usedTime) : '',
                    valueDisplay,
                    playerName,
                    statusLabel: STATUS_LABELS[statusKey] || statusKey,
                    statusClass: this.statusClass(statusKey),
                    statusIcon: this.statusIcon(statusKey),
                    selectable: this.isSelectable(rebate),
                    usesDisplay,
                    _rebate: rebate,
                };
            },
            statsFor(list) {
                return {
                    count: list.length,
                    value: list.reduce((sum, rebate) => sum + this.rebatePrice(rebate), 0),
                };
            },
            async generate() {
                this.errors = {};
                const count = Number(this.form.count);
                if (!count || count < 1 || count > 100) {
                    this.errors.count = 'Podaj ilość od 1 do 100';
                    return;
                }
                const payload = {
                    type: this.form.type,
                    count,
                };
                if (this.form.type === 'price' || this.form.type === 'mass') {
                    const value = Number(String(this.form.value).replace(',', '.'));
                    if (!value || value <= 0) {
                        this.errors.value = 'Podaj kwotę rabatu';
                        return;
                    }
                    payload.value = value;
                }
                if (this.form.type === 'mass') {
                    const maxUses = Number(this.form.maxUses);
                    if (!maxUses || maxUses < 1) {
                        this.errors.max_uses = 'Podaj maksymalną liczbę użyć';
                        return;
                    }
                    payload.max_uses = maxUses;
                    const validFrom = this.formatApiDate(this.form.validFrom, false);
                    const validTo = this.formatApiDate(this.form.validTo, true);
                    if (validFrom) payload.valid_from = validFrom;
                    if (validTo) payload.valid_to = validTo;
                }

                this.generating = true;
                try {
                    await generateRebates(this.id, payload);
                    this.resetGenerateForm();
                    await this.loadRebates();
                    this.$nextTick(() => this.scrollToTable());
                } catch (e) {
                    console.error('Error generating rebates', e);
                    this.errors = this.normalizeErrors(e);
                    alert(e?.error || e?.message || 'Nie udało się wygenerować kodów.');
                } finally {
                    this.generating = false;
                }
            },
            resetGenerateForm() {
                this.form = {
                    type: this.form.type,
                    value: '',
                    count: '',
                    maxUses: '',
                    validFrom: null,
                    validTo: null,
                };
                this.errors = {};
            },
            scrollToTable() {
                const el = this.$refs.rebatesTable;
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            },
            async saveCombine(value) {
                if (!this.features.combine || !this.combineReady) return;
                const enabled = value === 'sum';
                const current =
                    this.competitionSetting.rebatesCombine ?? this.competitionSetting.rebates_combine ?? false;
                const currentEnabled = current === true || current === 1 || current === '1';
                if (enabled === currentEnabled) return;
                try {
                    await updateRebatesCombine(this.id, enabled);
                    if (this.competitionSetting) {
                        this.$set(this.competitionSetting, 'rebatesCombine', enabled);
                    }
                } catch (e) {
                    console.error('Error saving rebatesCombine', e);
                    alert(e?.error || 'Nie udało się zapisać ustawienia sumowania.');
                    this.combineReady = false;
                    this.initCombineFromSettings();
                    this.$nextTick(() => {
                        this.combineReady = true;
                    });
                }
            },
            toggleSelectAll(event) {
                event.preventDefault();
                event.stopPropagation();
                if (this.selectAll) {
                    this.selectedIds = [];
                    return;
                }
                this.selectedIds = this.tableItems.filter((item) => item.selectable).map((item) => item.id);
            },
            toggleRow(id, event) {
                event.preventDefault();
                event.stopPropagation();
                if (this.selectedIds.includes(id)) {
                    this.selectedIds = this.selectedIds.filter((selectedId) => selectedId !== id);
                    return;
                }
                this.selectedIds = this.selectedIds.concat(id);
            },
            clearSelection() {
                this.selectedIds = [];
                this.selectAll = false;
                this.shareChecked = false;
            },
            syncShareCheckbox() {
                if (!this.selectedRebates.length) {
                    this.shareChecked = false;
                    return;
                }
                this.shareChecked = this.selectedRebates.every((rebate) => this.resolveStatusKey(rebate) === 'shared');
            },
            onSharePointer(event) {
                event.preventDefault();
                event.stopPropagation();
                this.requestShare();
            },
            onShareKey(event) {
                if (event.key !== ' ' && event.key !== 'Enter') return;
                event.preventDefault();
                event.stopPropagation();
                this.requestShare();
            },
            requestShare() {
                if (this.shareChecked || this.sharing) return;
                this.markSelectedShared();
            },
            async markSelectedShared() {
                if (!this.features.share || this.sharing || !this.selectedIds.length) return;
                this.sharing = true;
                this.shareChecked = true;
                try {
                    const result = await bulkUpdateRebatesStatus(this.id, this.selectedIds.slice());
                    if (result?.skipped > 0 && Array.isArray(result.errors) && result.errors.length) {
                        alert(result.errors.join('\n'));
                    }
                    await this.loadRebates({ preserveSelection: true });
                } catch (e) {
                    console.error('Error updating rebate status', e);
                    alert(e?.error || 'Nie udało się oznaczyć kodów.');
                    this.syncShareCheckbox();
                } finally {
                    this.sharing = false;
                }
            },
            exportCsv() {
                const rows = [
                    ['Data utw.', 'Wartość', 'Kod', 'Imię i nazwisko'],
                    ...this.selectedRebates.map((rebate) => {
                        const row = this.mapRebateRow(rebate);
                        return [row.addedDateDisplay, row.valueDisplay, row.code, row.playerName];
                    }),
                ];
                const csv = `\uFEFF${dataToCsv(rows)}`;
                downloadCsv('rabaty', csv);
            },
            copyCodes() {
                const text = this.selectedRebates
                    .map((rebate) => rebate.code || rebate.codeRaw)
                    .filter(Boolean)
                    .join(', ');
                if (!text) return;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
                } else {
                    this.fallbackCopy(text);
                }
            },
            fallbackCopy(text) {
                const el = document.createElement('textarea');
                el.value = text;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            },
            deleteSelected() {
                if (!this.features.delete) return;
                const ids = this.selectedRebates
                    .filter((rebate) => this.isDeletable(rebate))
                    .map((rebate) => rebate.id);
                if (!ids.length) {
                    alert('Można usunąć tylko kody ze statusem Dostępny.');
                    return;
                }
                this.pendingDeleteIds = ids;
                this.$refs.deleteModal.openModal();
            },
            closeDeleteModal() {
                this.pendingDeleteIds = [];
                if (this.$refs.deleteModal) {
                    this.$refs.deleteModal.closeModal();
                }
            },
            async confirmDelete() {
                if (!this.pendingDeleteIds.length || this.deleting) return;
                this.deleting = true;
                try {
                    const result = await deleteRebates(this.id, this.pendingDeleteIds);
                    if (result?.skipped > 0 && Array.isArray(result.errors) && result.errors.length) {
                        alert(result.errors.join('\n'));
                    }
                    this.closeDeleteModal();
                    this.pendingDeleteIds = [];
                    await this.loadRebates();
                } catch (e) {
                    console.error('Error deleting rebates', e);
                    alert(e?.error || 'Nie udało się usunąć kodów.');
                } finally {
                    this.deleting = false;
                }
            },
        },
    };
</script>

<style scoped>
    .competition-settings {
        width: 100%;
    }
    @media (min-width: 992px) {
        .competition-settings {
            margin-left: 30px;
        }
    }
    .rebates .form.rebates-form__box {
        padding: 24px 16px;
        overflow: visible;
    }
    .rebates-form__label,
    .rebates-form__hint,
    .rebates-field-label {
        margin: 0;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: var(--color-white);
    }
    .rebates-form__label {
        margin-bottom: 16px;
    }
    .rebates-field-label {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 8px;
    }
    .rebates-cycle {
        margin-top: 16px;
        padding: 12px 16px;
        background: #fff8e1;
        color: #455a64;
        border-radius: 4px;
        text-align: center;
        font-size: 14px;
        line-height: 20px;
    }
    .rebates-generate__row,
    .rebates-generate__mass {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 16px;
        margin-top: 16px;
    }
    .rebates-generate__amount,
    .rebates-generate__count,
    .rebates-generate__max {
        width: 69px;
    }
    .rebates-generate__amount.is-disabled {
        opacity: 0.45;
        pointer-events: none;
    }
    .rebates-generate__date {
        width: 160px;
    }
    .rebates-generate__mass >>> p {
        color: var(--color-white);
        font-size: 14px;
        line-height: 20px;
    }
    .rebates-generate__submit {
        margin-left: auto;
        flex: 0 1 326px;
        min-width: 200px;
    }
    .rebates-form__error {
        margin: 8px 0 0;
        color: #ff8a80;
        font-size: 13px;
        line-height: 18px;
    }
    @media (max-width: 767px) {
        .rebates-generate__submit {
            margin-left: 0;
            flex-basis: 100%;
            width: 100%;
        }
    }

    .rebate-list-card {
        background: var(--color-slate, #455a64);
        border-radius: 4px;
    }
    .rebate-stats {
        position: relative;
        z-index: 2;
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
        padding: 24px 16px 8px;
        color: var(--color-white);
    }
    @media (min-width: 992px) {
        .rebate-stats {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(280px, 1.15fr);
            padding: 24px 30px 8px;
        }
        .rebate-stats--selection {
            grid-template-columns: minmax(160px, 1fr) minmax(160px, 0.8fr) minmax(280px, 1.6fr);
        }
        .rebate-stats--selection-no-share {
            grid-template-columns: minmax(160px, 1fr) minmax(280px, 1.6fr);
        }
    }
    .rebate-stats__box {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 0;
    }
    .rebate-stats__title {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
    }
    .rebate-stats__title--ghost {
        visibility: hidden;
    }
    @media (max-width: 991px) {
        .rebate-stats__title--ghost {
            display: none;
        }
    }
    .rebate-metrics {
        display: flex;
        flex-wrap: wrap;
        gap: 24px;
    }
    .rebate-metric {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 72px;
        font-size: 14px;
        line-height: 20px;
    }
    .rebate-metric strong {
        font-weight: 700;
    }
    .rebate-filters {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 8px;
    }
    .rebate-filter {
        position: relative;
        width: auto;
        min-width: 0;
        max-width: 100%;
    }
    .rebate-filter__clear {
        position: absolute;
        left: 8px;
        top: 50%;
        z-index: 2;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #455a64;
        cursor: pointer;
    }
    .rebate-filter--filled >>> .multiselect__tags {
        padding-left: 28px;
    }
    .rebate-share >>> .input-checkbox {
        margin: 0;
    }
    .rebate-actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
    }
    .rebate-action {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--color-white);
        font-size: 14px;
        line-height: 20px;
        cursor: pointer;
        flex: 1 auto;
    }
    .rebate-action i {
        font-size: 12px;
    }
    .rebate-action--close {
        width: 46px;
        justify-content: center;
        text-align: center;
        text-transform: uppercase;
        line-height: 16px;
        white-space: normal;
    }

    .rebate-table-wrap {
        overflow-x: auto;
        background: var(--color-white, #fff);
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
    }
    .rebate-table {
        width: 100%;
        min-width: 880px;
        border-collapse: collapse;
        background: var(--color-white, #fff);
    }
    .rebate-table th,
    .rebate-table td {
        padding: 10px 8px;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        text-align: left;
        vertical-align: middle;
    }
    .rebate-table thead th {
        color: var(--color-white);
        background: var(--color-slate, #455a64);
        cursor: pointer;
        white-space: nowrap;
    }
    .rebate-table thead th.is-static {
        cursor: default;
    }
    .rebate-table tbody td {
        color: #455a64;
        background: var(--color-white, #fff);
        box-shadow: inset 0 -1px 0 rgba(84, 110, 122, 0.1);
    }
    .rebate-sort {
        margin-left: 6px;
        font-size: 12px;
    }
    .col-index {
        width: 40px;
        padding-left: 16px;
    }
    .col-addedDateDisplay {
        width: 110px;
    }
    .col-valueDisplay {
        width: 90px;
    }
    .col-code {
        width: 160px;
    }
    .col-usedDateDisplay {
        width: 110px;
    }
    .col-statusLabel {
        width: 150px;
    }
    .col-select {
        width: 48px;
        text-align: right;
        padding-right: 12px;
    }
    .rebate-code {
        letter-spacing: 0.02em;
    }
    .rebate-uses {
        display: block;
        font-size: 12px;
        line-height: 16px;
        color: #607d8b;
    }
    .rebate-status {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
    }
    .status-available {
        color: #50ae55;
    }
    .status-shared {
        color: #0098df;
    }
    .status-used {
        color: #e62e53;
    }
    .status-entered {
        color: #78909c;
    }
    .rebate-check {
        display: inline-flex;
    }
    .rebate-check >>> .panel-input-error,
    .rebate-share >>> .panel-input-error {
        display: none;
    }
    .rebate-check >>> .input-checkbox input[type='checkbox'] + span {
        min-width: 16px;
        min-height: 16px;
    }
    .rebate-empty {
        padding: 48px 0;
        text-align: center;
        font-style: italic;
        color: #455a64;
    }

    .rebate-delete-modal >>> .modal__dialog {
        width: 614px;
        max-width: calc(100vw - 32px);
        background: #eceff1;
    }
    .rebate-delete-modal >>> .modal__body {
        height: auto;
        background: #eceff1;
    }
    .rebate-delete-modal__body {
        padding: 32px 29px 8px;
        color: #455a64;
        font-size: 14px;
        line-height: 20px;
    }
    .rebate-delete-modal__body p + p {
        margin-top: 16px;
    }
    .rebate-delete-modal__cancel {
        min-width: 160px;
        height: 40px;
        padding: 8px 16px;
        border: 0;
        background: transparent;
        color: #78909c;
        font-size: 14px;
        line-height: 20px;
        text-transform: uppercase;
        cursor: pointer;
    }
</style>
